import FilterSelect from '@/components/inputs/FilterSelect';
import Input from '@/components/inputs/Input';
import PortalInputController from '@/components/inputs/PortalInputController';
import { Fragment, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';

import { Option } from '@/constants/general';

import { PortalWhitelistRequestFormValues } from '@/components/form/schema/portalWhitelistSchema';
import TextArea from '@/components/inputs/TextArea';
import { addSpacingToCharacter } from '@/utils/format';
import useLanguage from '@/utils/hooks/useLanguage';
import { useSso } from '@/utils/providers/SSOProvider';
import { getAdditionalAccountNumberLabel } from './BankDetails';

const currenciesToSupportAdditionalAccountNumber = ['USD', 'GBP', 'AUD', 'DKK'];

interface RequestBankInputsProps {}

const RequestBankInputs = {
    Section1: (props: RequestBankInputsProps) => {
        const lang = useLanguage();

        const { currenciesApi, countriesApi } = useSso();

        const { watch, trigger, setValue } = useFormContext<PortalWhitelistRequestFormValues>();

        const [accountNumber, swiftCode, selectedBaseCurrency, additionalAccountNumber] = watch([
            'accountNumber',
            'swiftCode',
            'baseCurrencyId',
            'additionalAccountNumber'
        ]);

        const { data: currencies, isLoading: isLoadingCurrencies } = currenciesApi;

        const { data: countriesData, isLoading: isLoadingCountriesData } = countriesApi;

        const listOfCountriesOptions: Option[] = useMemo(() => {
            return (
                countriesData?.map((country) => ({
                    label: country.name,
                    value: country.name
                })) || []
            );
        }, [countriesData, isLoadingCountriesData]);

        const currencyOptions: Option[] = useMemo(() => {
            if (!currencies || !currencies || isLoadingCurrencies) {
                return [];
            }

            return (
                currencies &&
                currencies
                    .filter(
                        (currency) =>
                            currency.enabledForDeposit && currency.enabledForWithdrawal && currency.type === 'FIAT'
                    )
                    .map((currency) => {
                        return {
                            label: currency.digitalAssetName || currency.name,
                            value: currency.id
                        };
                    })
            );
        }, [currencies, isLoadingCurrencies]);

        const additionalAccountNumberLabel = useMemo(() => {
            return getAdditionalAccountNumberLabel(selectedBaseCurrency?.label || '', lang);
        }, [lang, selectedBaseCurrency]);

        const showAdditionalAccountNumber = useMemo(() => {
            return (
                selectedBaseCurrency &&
                currenciesToSupportAdditionalAccountNumber.includes(selectedBaseCurrency.label || '')
            );
        }, [selectedBaseCurrency]);

        const { isUSD, isDKK } = useMemo(() => {
            return {
                isUSD: selectedBaseCurrency?.label === 'USD',
                isDKK: selectedBaseCurrency?.label === 'DKK'
            };
        }, [selectedBaseCurrency]);

        const isSwiftCodeRequired = useMemo(() => {
            if (isUSD) {
                return !additionalAccountNumber;
            } else {
                return true;
            }
        }, [isUSD, additionalAccountNumber]);

        const isAdditionalAccountNumberRequired = useMemo(() => {
            if (isUSD) {
                return !swiftCode;
            } else if (isDKK) {
                return !!accountNumber?.trim();
            } else {
                return false;
            }
        }, [swiftCode, isUSD, isDKK, accountNumber]);

        return (
            <Fragment>
                <fieldset className="space-y-3">
                    <legend className="text-xs text-brand-neutral-500">{lang.portal.generalInformation}</legend>
                    <PortalInputController
                        name="friendlyName"
                        label={lang.portal.friendlyName}
                        placeholder={lang.portal.friendlyName}
                        required>
                        <Input />
                    </PortalInputController>
                    <PortalInputController
                        name="baseCurrencyId"
                        label={lang.lite.currency}
                        placeholder={lang.lite.currency}
                        asyncInput={isLoadingCurrencies}
                        required>
                        <FilterSelect options={currencyOptions} enableCurrencyIcon enableSorting />
                    </PortalInputController>
                </fieldset>
                <fieldset className="space-y-3 mt-3">
                    <legend className="text-xs text-brand-neutral-500">{lang.portal.bankAccountInformation}</legend>
                    <div className="flex flex-col xsm:flex-row gap-4 w-full">
                        <div className="flex-1">
                            <PortalInputController
                                name="bankName"
                                label={lang.portal.bankName}
                                placeholder={lang.portal.bankName}
                                required>
                                <Input />
                            </PortalInputController>
                        </div>
                        <div className="flex-1">
                            <PortalInputController
                                name="beneficiaryName"
                                label={lang.portal.beneficiaryName}
                                placeholder={lang.portal.beneficiaryName}>
                                <Input />
                            </PortalInputController>
                        </div>
                    </div>
                </fieldset>
                <fieldset>
                    <div className="flex flex-col xsm:flex-row gap-4 w-full">
                        <div className="flex-1">
                            <PortalInputController
                                name="bankAddress"
                                label={lang.portal.bankAddress}
                                placeholder={lang.portal.bankAddress}
                                required>
                                <Input />
                            </PortalInputController>
                        </div>
                    </div>
                </fieldset>
                <fieldset>
                    <div className="flex flex-col xsm:flex-row gap-4 w-full">
                        <div className="flex-1">
                            <PortalInputController
                                name="country"
                                label={lang.portal.country}
                                placeholder={lang.portal.country}
                                required>
                                <FilterSelect options={listOfCountriesOptions} />
                            </PortalInputController>
                        </div>
                        <div className="flex-1">
                            <PortalInputController
                                name="swiftCode"
                                label="SWIFT/BIC"
                                placeholder="SWIFT/BIC"
                                required={isSwiftCodeRequired}>
                                <Input />
                            </PortalInputController>
                        </div>
                    </div>
                </fieldset>
                <fieldset className="space-y-3">
                    <div className="flex flex-col xsm:flex-row gap-4 w-full">
                        <div className="flex-1">
                            <PortalInputController
                                name="ibanCode"
                                label="IBAN"
                                placeholder="IBAN"
                                onChange={(e) => {
                                    setValue('ibanCode', addSpacingToCharacter(e.target.value, 4), {
                                        shouldDirty: true,
                                        shouldValidate: true
                                    });
                                    trigger('accountNumber');
                                }}>
                                <Input />
                            </PortalInputController>
                        </div>
                        <div className="flex-1">
                            <PortalInputController
                                name="accountNumber"
                                label={lang.portal.accountNumber}
                                placeholder={lang.portal.accountNumber}
                                onChange={(e) => {
                                    setValue('accountNumber', e.target.value, {
                                        shouldDirty: true,
                                        shouldValidate: true
                                    });
                                    trigger('ibanCode');
                                }}>
                                <Input />
                            </PortalInputController>
                        </div>
                    </div>
                </fieldset>
                <fieldset>
                    {showAdditionalAccountNumber && (
                        <PortalInputController
                            name="additionalAccountNumber"
                            label={additionalAccountNumberLabel}
                            placeholder={additionalAccountNumberLabel}
                            onChange={(e) => {
                                setValue('additionalAccountNumber', e.target.value, {
                                    shouldDirty: true,
                                    shouldValidate: true
                                });
                                trigger('swiftCode');
                            }}
                            required={isAdditionalAccountNumberRequired}>
                            <Input />
                        </PortalInputController>
                    )}
                </fieldset>
                <fieldset>
                    <PortalInputController
                        name="requestDescription"
                        label={lang.portal.requestDescription}
                        placeholder={lang.portal.requestDescription}>
                        <TextArea rows={2} />
                    </PortalInputController>
                </fieldset>
            </Fragment>
        );
    },
    Section2: (props: RequestBankInputsProps) => {
        const lang = useLanguage();
        return (
            <Fragment>
                <fieldset className="space-y-3">
                    <legend className="text-xs text-brand-neutral-500">{lang.portal.correspondentInformation}</legend>
                    <div className="flex flex-col xsm:flex-row gap-4 w-full">
                        <div className="flex-1">
                            <PortalInputController
                                name="correspondentBankName"
                                label={lang.portal.bankName}
                                placeholder={lang.portal.bankName}>
                                <Input />
                            </PortalInputController>
                        </div>
                        <div className="flex-1">
                            <PortalInputController
                                name="correspondentBankSwiftCode"
                                label={lang.portal.swiftCode}
                                placeholder={lang.portal.swiftCode}>
                                <Input />
                            </PortalInputController>
                        </div>
                    </div>
                </fieldset>

                <fieldset className="space-y-3 mt-3 w-full">
                    <legend className="text-xs text-brand-neutral-500">{lang.portal.intermediaryInformation}</legend>
                    <div className="flex flex-col xsm:flex-row gap-4 w-full">
                        <div className="flex-1">
                            <PortalInputController
                                name="intermediaryBankName"
                                label={lang.portal.bankName}
                                placeholder={lang.portal.bankName}>
                                <Input />
                            </PortalInputController>
                        </div>
                        <div className="flex-1">
                            <PortalInputController
                                name="intermediaryBankSwiftCode"
                                label={lang.portal.swiftCode}
                                placeholder={lang.portal.swiftCode}>
                                <Input />
                            </PortalInputController>
                        </div>
                    </div>
                </fieldset>
            </Fragment>
        );
    }
};

export default RequestBankInputs;
